/**
 * Project Realism — can this project currently finish on time?
 *
 * Renders the backend's verdict (REALISTIC / CONDITIONALLY REALISTIC /
 * AT RISK / INFEASIBLE / UNKNOWN) and canFinishOnTime (YES / NO / UNKNOWN)
 * with its stated basis. Approved finish, forecast finish, and variance stay
 * separate — and a failed call renders as a failure, never as "healthy".
 */
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  SectionLoading,
  SectionError,
  FeatureDisabled,
  UnknownValue,
} from "../../components/workflow/WorkflowStates";
import { realismDisplay } from "./readinessPresentation";
import { CalendarClock, RefreshCw, AlertTriangle, LifeBuoy } from "lucide-react";

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return String(value);
  }
};

const CAN_FINISH_STYLES = {
  YES: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  NO: "bg-red-500/20 text-red-400 border-red-500/30",
  UNKNOWN: "bg-muted text-muted-foreground border-border",
};

const describeRow = (row) => {
  if (typeof row === "string") return row;
  // criticalMaterials: {materialStatusId, instanceId, material, procurementStatus, expectedDelivery}
  if (row.material) {
    return `${row.material} — ${row.procurementStatus || "procurement status unknown"}${
      row.expectedDelivery ? `, expected ${row.expectedDelivery}` : ""
    }`;
  }
  // criticalRequirements / criticalReviews / negativeFloat / laborIssues carry
  // titles or details; fall back to any human-readable field before giving up.
  return (
    row.detail ||
    row.reason ||
    row.title ||
    row.name ||
    row.requirementTitle ||
    Object.values(row).find((v) => typeof v === "string") ||
    "—"
  );
};

const ListBlock = ({ title, rows, tone = "text-muted-foreground", cap = 4 }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-semibold text-muted-foreground">{title}</p>
      {rows.slice(0, cap).map((row, i) => (
        <p key={i} className={`text-xs ${tone}`}>
          {describeRow(row)}
        </p>
      ))}
      {rows.length > cap && (
        <p className="text-[10px] text-muted-foreground">+{rows.length - cap} more</p>
      )}
    </div>
  );
};

const RealismPanel = ({ realism, loading, classified, onRetry, compact = false }) => {
  if (loading) return <SectionLoading label="Assessing whether the project can finish on time…" />;
  if (classified?.kind === "disabled")
    return (
      <FeatureDisabled
        feature="Project realism"
        hint="The realism assessment is built but execution intelligence is not enabled for this environment."
      />
    );
  if (classified)
    return <SectionError error={classified} onRetry={onRetry} label="Realism failed to load — schedule truth is unknown right now, not healthy" />;
  if (!realism) return null;

  const display = realismDisplay(realism.classification);
  const canFinish = realism.canFinishOnTime || "UNKNOWN";

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-primary" />
            Project Realism
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`${display.className} font-medium`}>
              <display.Icon className="w-3 h-3 mr-1" />
              {display.label}
            </Badge>
            <Badge
              variant="outline"
              className={`${CAN_FINISH_STYLES[canFinish] || CAN_FINISH_STYLES.UNKNOWN} font-medium`}
              title={realism.basis || undefined}
            >
              Can finish on time: {canFinish}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {realism.basis && <p className="text-xs text-muted-foreground">{realism.basis}</p>}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Approved finish</p>
            <p className="text-sm font-semibold">
              {fmtDate(realism.approvedFinish) || <UnknownValue reason="No approved finish date is on record." />}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Forecast finish</p>
            <p className="text-sm font-semibold">
              {fmtDate(realism.forecastFinish) || <UnknownValue reason="The forecast did not resolve." />}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Variance</p>
            <p className={`text-sm font-semibold ${realism.varianceDays > 0 ? "text-red-400" : realism.varianceDays < 0 ? "text-emerald-400" : ""}`}>
              {realism.varianceDays == null ? (
                <UnknownValue />
              ) : (
                `${realism.varianceDays > 0 ? "+" : ""}${realism.varianceDays} days`
              )}
            </p>
          </div>
        </div>

        {!compact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(realism.criticalPath || []).length > 0 && (
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-muted-foreground">Critical path</p>
                <p className="text-xs text-muted-foreground">
                  {realism.criticalPath.length} workface
                  {realism.criticalPath.length === 1 ? "" : "s"} govern the finish date — see
                  What Can Start below for each one's gates.
                </p>
              </div>
            )}
            <ListBlock title={`Negative float (${(realism.negativeFloat || []).length})`} rows={realism.negativeFloat} tone="text-red-300/90" />
            <ListBlock title="Critical requirements" rows={realism.criticalRequirements} tone="text-amber-300/90" />
            <ListBlock title="Critical materials" rows={realism.criticalMaterials} tone="text-amber-300/90" />
            <ListBlock title="Unresolved reviews" rows={realism.criticalReviews} tone="text-amber-300/90" />
            <ListBlock title="Labor / resource gaps" rows={realism.laborIssues} tone="text-amber-300/90" />
          </div>
        )}

        {(realism.missingInformation || []).length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-0.5">
            <p className="text-xs font-semibold text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" />
              What the assessment does not know
            </p>
            {realism.missingInformation.slice(0, 5).map((m, i) => (
              <p key={i} className="text-xs text-muted-foreground">{m}</p>
            ))}
          </div>
        )}

        {realism.recovery && Object.keys(realism.recovery).length > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <LifeBuoy className="w-3.5 h-3.5" />
            {realism.recovery.available
              ? `${realism.recovery.openProposals} recovery proposal${
                  realism.recovery.openProposals === 1 ? "" : "s"
                } open — review in the Control Tower. The approved baseline changes only on an explicit decision.`
              : realism.recovery.recommended
                ? "Recovery is recommended and no proposal is open yet — propose one from the Control Tower."
                : "No recovery proposal is open, and none is currently indicated."}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default RealismPanel;

/** Small inline refresh control for the recalculation chain (C6). */
export const RecalculateButton = ({ onClick, busy }) => (
  <Button
    variant="outline"
    size="sm"
    className="h-8 text-xs"
    onClick={onClick}
    disabled={busy}
    title="Recalculates derived intelligence (readiness → ERS → CPM → realism). It does not apply schedule changes."
  >
    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
    Refresh execution intelligence
  </Button>
);
